// ============================================================
// CF.page — Page bootstrap factory
// ============================================================
// Replaces the boilerplate at the top of every Setting page:
//
//   $(document).ready(function () {
//       myTable = CF.dataTable.serverInit('#myTable', { columns, ... });
//       CF.select.init('#filter-username', { ... });
//       // ... 5 more filter inits ...
//   });
//
// With a single declarative call:
//
//   import { initPage } from './cf/page.js';
//   initPage({
//       tableId: '#userTable',
//       columns: [...],
//       primaryAction: { label: 'Add', onClick: openCreateModal },
//       rowActions:    [...],
//       mapItem:       fn,
//       loadFn:        fn,
//       filters: [
//           { id: 'f-username', mode: 'multi', loadFn: ... },
//           { id: 'f-email',    mode: 'multi', loadFn: ... }
//       ]
//   });
//
// Returns the DataTable handle for further use by submit/edit handlers.
// ============================================================

const CF = window.CF = window.CF || {};

/**
 * Wait for jQuery DOM ready, then initialize a server-side DataTable
 * and any declarative filter `cf-select` instances.
 *
 * @param {Object} cfg
 * @param {string} cfg.tableId       - jQuery selector for table element
 * @param {Array}  cfg.columns       - DataTable columns config
 * @param {Object} [cfg.primaryAction]
 * @param {Array}  [cfg.actions]
 * @param {Array}  [cfg.rowActions]
 * @param {Function} [cfg.onRowAction]
 * @param {Function} [cfg.mapItem]
 * @param {Function} [cfg.loadFn]
 * @param {Array}  [cfg.filters]     - [{ id, mode, loadFn, ... }] passed to CF.select.init
 * @param {Function} [cfg.onReady]   - Extra hook after init completes
 * @returns {Object} the DataTable handle returned by CF.dataTable.serverInit
 */
export function initPage(cfg) {
    let tableHandle = null;

    const boot = function () {
        const dtCfg = {
            columns:       cfg.columns,
            primaryAction: cfg.primaryAction,
            actions:       cfg.actions || [],
            rowActions:    cfg.rowActions,
            onRowAction:   cfg.onRowAction,
            mapItem:       cfg.mapItem,
            loadFn:        cfg.loadFn
        };

        tableHandle = CF.dataTable.serverInit(cfg.tableId, dtCfg);

        if (Array.isArray(cfg.filters)) {
            cfg.filters.forEach(function (f) {
                if (f && f.id) CF.select.init('#' + f.id, f);
            });
        }

        if (typeof cfg.onReady === 'function') cfg.onReady(tableHandle);
    };

    // jQuery may not be loaded if this is called before the bundle ready event.
    if (window.jQuery) {
        window.jQuery(boot);
    } else {
        document.addEventListener('DOMContentLoaded', boot);
    }

    // For callers that want to grab the handle synchronously (legacy pattern
    // assigns to a `var myTable = null` and reads it later), we expose a getter.
    return {
        get table() { return tableHandle; },
        reload(args) { if (tableHandle) tableHandle.reload(args); }
    };
}

/**
 * Helper: clear a list of input ids (handy at the start of openCreateModal).
 *   resetFields(['c-name', 'c-email', 'c-password']);
 */
export function resetFields(ids) {
    if (!Array.isArray(ids)) return;
    ids.forEach(function (id) {
        const el = document.getElementById(id);
        if (!el) return;
        if (el.type === 'checkbox' || el.type === 'radio') {
            el.checked = false;
        } else {
            el.value = '';
        }
    });
}

/**
 * Helper: read trimmed string values from a list of input ids into an object.
 *   const body = readFields({ name: 'c-name', email: 'c-email' });
 */
export function readFields(idMap) {
    const out = {};
    if (!idMap) return out;
    Object.keys(idMap).forEach(function (key) {
        const el = document.getElementById(idMap[key]);
        if (!el) { out[key] = null; return; }
        if (el.type === 'checkbox') { out[key] = !!el.checked; return; }
        const v = (el.value || '').trim();
        out[key] = v === '' ? null : v;
    });
    return out;
}

// Expose on window.CF
CF.page = { init: initPage, resetFields, readFields };
